import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import { Helmet } from 'react-helmet';
import { useApiMutation } from 'src/hooks/useApiMutation';

export enum PipelineStepId {
  DATA_PROCESSING = 'DATA_PROCESSING',
  ERP_COMPONENT_DETECTION = 'ERP_COMPONENT_DETECTION',
  BRAIN_YEARS_SCORING = 'BRAIN_YEARS_SCORING',
  METRIC_BENCHMARKING = 'METRIC_BENCHMARKING',
  RECOMMENDATION_GEN = 'RECOMMENDATION_GEN',
  REPORT_GEN = 'REPORT_GEN'
}

export enum Scope {
  CLINIC = 'CLINIC',
  PERSONAL = 'PERSONAL'
}

const PERSONAL_PIPELINE_STEPS: PipelineStepId[] = [
  PipelineStepId.DATA_PROCESSING,
  PipelineStepId.ERP_COMPONENT_DETECTION
];

// Sets UserAssessment.buttonsReversed. KEEP omits the field so the stored value is left
// alone; sending false on every reprocess would silently clear a reversed flag.
export enum ButtonMapping {
  KEEP = 'KEEP',
  REVERSED = 'REVERSED',
  NORMAL = 'NORMAL'
}

const pipelineStepsForScope = (
  scope: Scope,
  mapping: ButtonMapping
): PipelineStepId[] => {
  // The flag is only read by DATA_PROCESSING; the API rejects it with any later step.
  if (mapping !== ButtonMapping.KEEP) return [PipelineStepId.DATA_PROCESSING];
  return scope === Scope.PERSONAL
    ? PERSONAL_PIPELINE_STEPS
    : (Object.values(PipelineStepId) as PipelineStepId[]);
};

const formatError = (err: any): string => {
  if (!err) return '';
  if (typeof err === 'string') return err;
  const parts: string[] = [];
  if (Array.isArray(err.graphQLErrors) && err.graphQLErrors.length > 0) {
    parts.push(
      ...err.graphQLErrors.map((e: any) => e?.message ?? String(e))
    );
  }
  if (err.networkError) {
    const ne = err.networkError;
    const neResult = ne?.result;
    if (neResult) {
      try {
        parts.push(
          typeof neResult === 'string' ? neResult : JSON.stringify(neResult)
        );
      } catch {
        parts.push(String(ne));
      }
    } else if (ne.message) {
      parts.push(ne.message);
    }
  }
  if (parts.length === 0 && err.message) {
    parts.push(err.message);
  }
  if (parts.length === 0) {
    try {
      parts.push(JSON.stringify(err));
    } catch {
      parts.push(String(err));
    }
  }
  return parts.join(' | ');
};

const ReprocessAssessment: React.FC<{}> = () => {
  const [userAssessmentId, setUserAssessmentId] = useState<string>('');
  const [scope, setScope] = useState<Scope>(Scope.CLINIC);
  const [fromStep, setFromStep] = useState<PipelineStepId | ''>('');
  const [buttonMapping, setButtonMapping] = useState<ButtonMapping>(
    ButtonMapping.KEEP
  );
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [thrownError, setThrownError] = useState<string | null>(null);

  const availableSteps = pipelineStepsForScope(scope, buttonMapping);

  useEffect(() => {
    if (fromStep && !availableSteps.includes(fromStep)) {
      setFromStep('');
    }
  }, [scope, buttonMapping]);

  const [executeReprocess, { loading, data, error }] = useApiMutation(
    'assessment',
    'ReprocessUserAssessment'
  );

  useEffect(() => {
    if (data || error) {
      setSubmitted(true);
    }
  }, [data, error]);

  const canSubmit = userAssessmentId.trim().length > 0 && !loading;

  const submit = async () => {
    setSubmitted(false);
    setThrownError(null);
    try {
      await executeReprocess({
        variables: {
          id: userAssessmentId.trim(),
          scope,
          ...(fromStep ? { fromStep } : {}),
          ...(buttonMapping !== ButtonMapping.KEEP
            ? { buttonsReversed: buttonMapping === ButtonMapping.REVERSED }
            : {})
        }
      });
    } catch (e) {
      setThrownError(formatError(e));
      setSubmitted(true);
    }
  };

  const displayedError =
    thrownError || (error ? formatError(error) : null);

  return (
    <>
      <Helmet>
        <title>Dashboard: Data Tools - Reprocess Assessment | Sens.ai</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: 'background.default',
          minHeight: '100%',
          py: 8
        }}
      >
        <Container maxWidth="md">
          <Typography color="textPrimary" variant="h5" sx={{ mb: 3 }}>
            Data Tools - Reprocess Assessment
          </Typography>
          <Card>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="User Assessment ID"
                    name="userAssessmentId"
                    variant="outlined"
                    fullWidth
                    required
                    value={userAssessmentId}
                    onChange={(e) => setUserAssessmentId(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="scope-label">Scope</InputLabel>
                    <Select
                      labelId="scope-label"
                      label="Scope"
                      value={scope}
                      onChange={(e) => setScope(e.target.value as Scope)}
                    >
                      {Object.values(Scope).map((s) => (
                        <MenuItem key={s} value={s}>
                          {s}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="from-step-label">From Step</InputLabel>
                    <Select
                      labelId="from-step-label"
                      label="From Step"
                      value={fromStep}
                      onChange={(e) =>
                        setFromStep(
                          e.target.value as PipelineStepId | ''
                        )
                      }
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {availableSteps.map((step) => (
                        <MenuItem key={step} value={step}>
                          {step}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    color="textSecondary"
                    variant="body2"
                    sx={{ mb: 1 }}
                  >
                    Button Mapping
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    color="primary"
                    value={buttonMapping}
                    onChange={(_e, value: ButtonMapping | null) =>
                      value && setButtonMapping(value)
                    }
                  >
                    <ToggleButton value={ButtonMapping.KEEP}>
                      Keep stored
                    </ToggleButton>
                    <ToggleButton value={ButtonMapping.REVERSED}>
                      Reversed (L/R swapped)
                    </ToggleButton>
                    <ToggleButton value={ButtonMapping.NORMAL}>
                      Normal
                    </ToggleButton>
                  </ToggleButtonGroup>
                  <Typography
                    color="textSecondary"
                    variant="caption"
                    display="block"
                    sx={{ mt: 1 }}
                  >
                    Reversed scores each left press as right and vice versa.
                    Setting a mapping re-runs from DATA_PROCESSING.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    color="primary"
                    variant="contained"
                    disabled={!canSubmit}
                    onClick={submit}
                  >
                    {loading ? 'Reprocessing...' : 'Reprocess'}
                  </Button>
                </Grid>
                {submitted && displayedError && (
                  <Grid item xs={12}>
                    <Typography
                      color="error"
                      sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                    >
                      {displayedError}
                    </Typography>
                  </Grid>
                )}
                {submitted && !displayedError && data && (
                  <Grid item xs={12}>
                    <Typography color="primary">
                      Reprocess requested.
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Container>
      </Box>
    </>
  );
};

export default ReprocessAssessment;
